<template>
  <div class="editProperties">
    <div class="editProperties-title">
      <span>Properties</span>
    </div>
    <div class="editProperties-content"
      v-loading="updatePropertyLoading || deletePropertyLoading"
    >
      <div class="editProperties-property"
        v-for="property in properties"
        :key="property.name"
      >
        <edit-properties-property :property="property"
          :updatePropertyLoading="updatePropertyLoading"
          @action:deleteProperty="onDeleteProperty"
          @action:updateProperty="onUpdateProperty"
        />
        <div class="editProperties-property-values">
          <pill v-for="propertyValue in valuesForKey(property.name)"
            :key="propertyValue"
            clearable
            @action:delete="() => onRemoveValue(property, propertyValue)"
          >
            {{ propertyValue }}
          </pill>
          <tag-input @action:submit="v => onAddValue(property, v)"
            placeholder="Enter Property Value"
          />
        </div>
      </div>
      <div class="editProperties-addProperty">
        <div class="editProperties-addProperty-controls"
          v-if="isAddingProperty"
          v-loading="createPropertyLoading"
        >
          <neb-input placeholder="Enter Property Name"
            @keyup.enter.native="onCreatePropertyEnter"
          />
          <neb-icon name="x" height="16px" width="16px"
            @click.native="isAddingProperty = false"
          />
        </div>
        <neb-button type="textPrimary"
          @click="isAddingProperty = true"
        >
          + Property
        </neb-button>
      </div>
    </div>
  </div>
</template>

<script>
import Pill from './Pill.vue'
import TagInput from './TagInput.vue'
import NebButton from '@/components/Nebula/controls/Button'
import NebInput from '@/components/Nebula/controls/Input'
import NebIcon from '@/components/Nebula/Icon'
import { uniq } from 'lodash'
import { inject, ref } from '@vue/composition-api'
import store from '@/store'
import EditPropertiesProperty from './EditPropertiesProperty.vue'
import { useToast } from 'vue-toastification/composition'

export default {
  name: 'EditProperties',

  components: {
    Pill,
    TagInput,
    NebButton,
    NebInput,
    NebIcon,
    EditPropertiesProperty
  },

  props: {
    assetProperties: {
      type: Array,
      default: () => []
    }
  },

  emits: [
    'action:addValue',
    'action:removeValue'
  ],

  setup (props, { emit }) {
    const toast = useToast()
    const properties = inject('properties')
    const isAddingProperty = ref(false)

    const valuesForKey = propertyName => uniq(
      props.assetProperties
        .filter(p => p.property__name === propertyName)
        .map(p => p.value)
    )

    const onAddValue = (property, value) => emit('action:addValue', { property, value })
    const onRemoveValue = (property, value) => emit('action:removeValue', { property, value })
    const onCreatePropertyEnter = async e => {
      try {
        await properties.create({
          brandId: store.state.brand.settings.id,
          name: e.target.value
        })
        toast.success('Property created')
      } catch (error) {
        console.error(error)
        toast.error('Error creating property')
      } finally {
        await properties.loadList({ brandId: store.state.brand.settings.id })
        isAddingProperty.value = false
      }
    }
    const onDeleteProperty = async property => {
      try {
        await properties.performDelete(property.pk)
        toast.success('Property deleted')
      } catch (error) {
        console.error(error)
        toast.error('Error deleting property')
      } finally {
        await properties.loadList({ brandId: store.state.brand.settings.id })
      }
    }
    const onUpdateProperty = async ({ property, value }) => {
      try {
        await properties.update({ pk: property.pk, name: value })
        toast.success('Property updated')
      } catch (error) {
        console.error(error)
        toast.error('Error updating property')
      } finally {
        await properties.loadList({ brandId: store.state.brand.settings.id })
      }
    }

    return {
      valuesForKey,
      properties: properties.list,
      onAddValue,
      onRemoveValue,
      isAddingProperty,
      onCreatePropertyEnter,
      createPropertyLoading: properties.createLoading,
      onDeleteProperty,
      onUpdateProperty,
      updatePropertyLoading: properties.updateLoading,
      deletePropertyLoading: properties.deleteLoading
    }
  }
}
</script>

<style lang="scss">
@import '../Nebula/style.scss';

.editProperties {
  &-title {
    padding-bottom: 5px;
    border-bottom: 1px solid $very-light-pink;
    margin-bottom: 10px;

    span {
      margin: 0;
      font-family: $defaultFont;
      font-size: 12px;
      font-weight: 400;
      color: $slate-grey;
    }
  }

  &-property {
    &-values {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 5px;
      margin-bottom: 15px;
    }
  }

  &-addProperty {
    &-controls {
      display: flex;
      align-items: center;
      column-gap: 10px;
      padding-bottom: 10px;

      .nebIcon {
        cursor: pointer;
        color: $slate-grey;
      }
    }
  }
}
</style>
